/**
 * Assessments (PRD-1703, Navy Phase 0 handoff "Assessments"): one assessment
 * per source file, run against a scenario. A source is a recording or a
 * transcript of one, never both; the extension decides which.
 *
 * There is no Assessments API yet, so the model holds the records itself,
 * seeded locally, and simulates the transfer and the processing on a timer.
 * Scenarios come from the LibraryModel; their Usage is the active assessments
 * run against each, pushed back to it on every change.
 */
import { formatFileSize, plural } from './LibraryModel';

/* Uploading and Queued are transfer states with no equivalent in the Dataset
   Status Model, so they take the Standard chip; Processing, Completed and
   Failed take its processing, success and error. Failed carries the step it
   failed at in `failedStep`, so Retry re-runs that step and not the chain. */
export const ASSESSMENT_STATUS = {
  uploading: { label: 'Uploading', variant: 'standard', actions: [] },
  queued: { label: 'Queued', variant: 'standard', actions: ['archive'] },
  processing: { label: 'Processing', variant: 'processing', actions: ['archive'] },
  completed: { label: 'Completed', variant: 'success', actions: ['archive'] },
  failed: { label: 'Failed', variant: 'error', actions: ['retry', 'archive'] },
};

/** The ladder, in order: the status filter lists it this way. */
export const ASSESSMENT_STATUS_ORDER = ['uploading', 'queued', 'processing', 'completed', 'failed'];

export const AUDIO_EXTENSIONS = ['mp3', 'wav'];
// The document list until the transcript format is settled (handoff questions.md, 2).
export const TRANSCRIPT_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'];
export const SOURCE_FILE_LABEL = 'MP3, WAV or PDF, Word, Excel, CSV';
export const SOURCE_FILE_ACCEPT = '.mp3,.wav,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv';
export const MAX_SOURCE_FILE_MB = 250; // a 90-minute WAV is the sizing case

export const DEFAULT_ASSESSMENT_FILTERS = {
  scenario: 'all',
  status: 'all',
  visibility: 'active',
  sort: 'date_desc',
  search: '',
};

/* The toolbar's Sort drives the grid; in the list the headers sort, and the two
   stay in step where they overlap: name, upload date and duration. */
const SORT_TO_COLUMN = {
  name_asc: { key: 'name', dir: 'asc' },
  name_desc: { key: 'name', dir: 'desc' },
  date_asc: { key: 'uploaded', dir: 'asc' },
  date_desc: { key: 'uploaded', dir: 'desc' },
  dur_asc: { key: 'duration', dir: 'asc' },
  dur_desc: { key: 'duration', dir: 'desc' },
};

export function assessmentColumnSort(sort) {
  return SORT_TO_COLUMN[sort] || SORT_TO_COLUMN[DEFAULT_ASSESSMENT_FILTERS.sort];
}

export function assessmentToolbarSort(columnSort, current) {
  const hit = Object.keys(SORT_TO_COLUMN)
    .find((k) => SORT_TO_COLUMN[k].key === columnSort.key && SORT_TO_COLUMN[k].dir === columnSort.dir);
  return hit || current;
}

/* How long a record rests in each waiting state before the engine moves it on.
   A demo convenience, not a model of real transcription. */
const STAGE_MS = { queued: 3500, processing: 6000 };
const TICK_MS = 700;
const HOUR = 3600000;
const DAY = 86400000;
const UPLOAD_FAILED = 'Upload failed. The transfer was interrupted when the session expired.';

/**
 * The handoff's six: one of each state the ladder can rest in, both failure
 * steps, and one built on a transcript (no duration). Uploading isn't seeded:
 * the engine would carry it away within seconds. Scenario ids are the
 * Library's seeded scenarios. Dated from load; names are invented.
 */
export function seedAssessments(now = Date.now()) {
  const ago = (ms) => new Date(now - ms).toISOString();
  return [
    { id: 'a1', name: 'Strait Transit - Bravo Watch Run A', scenarioId: 's1', fileName: 'OOW_Strait_Transit_2026-09-18_RunA.wav', source: 'audio', durationSec: 4980, uploadedAt: ago(DAY + 5 * HOUR), status: 'queued', progress: 100 },
    { id: 'a2', name: 'Strait Transit - Charlie Watch Run B', scenarioId: 's1', fileName: 'OOW_Strait_Transit_2026-09-18_RunB.wav', source: 'audio', durationSec: 89, uploadedAt: ago(DAY + 2 * HOUR), status: 'processing', progress: 100 },
    { id: 'a3', name: 'Restricted Visibility - Bravo Watch', scenarioId: 's2', fileName: 'OOW_Restricted_Visibility_2026-09-19.mp3', source: 'audio', durationSec: 89, uploadedAt: ago(5 * HOUR), status: 'completed', progress: 100 },
    { id: 'a4', name: 'Man Overboard - Delta Watch', scenarioId: 's3', fileName: 'OOW_Man_Overboard_2026-09-19.mp3', source: 'audio', durationSec: 3960, uploadedAt: ago(14 * 60000), status: 'failed', failedStep: 'upload', progress: 62, error: UPLOAD_FAILED },
    { id: 'a5', name: 'Man Overboard - Alpha Watch', scenarioId: 's3', fileName: 'OOW_Man_Overboard_2026-09-17_transcript.docx', source: 'transcript', durationSec: null, uploadedAt: ago(2 * DAY + 6 * HOUR), status: 'completed', progress: 100 },
    { id: 'a6', name: 'Restricted Visibility - Charlie Watch', scenarioId: 's2', fileName: 'OOW_Restricted_Visibility_2026-09-16.wav', source: 'audio', durationSec: 4680, uploadedAt: ago(3 * DAY + 2 * HOUR), status: 'failed', failedStep: 'processing', progress: 100, error: 'Processing failed. The recording was transcribed but the comparison did not complete.' },
  ];
}

export const isTranscriptSource = (a) => a.source === 'transcript';

/** "83m", or "-" when there is no run time (a transcript carries none). */
export function formatDuration(sec) {
  if (!sec && sec !== 0) return '-';
  return `${Math.round(sec / 60)}m`;
}

export const assessmentStatusLabel = (a) => (ASSESSMENT_STATUS[a.status] || { label: a.status }).label;

function inVisibility(a, visibility) {
  if (visibility === 'archived') return !!a.archived;
  if (visibility === 'all') return true;
  return !a.archived;
}

/** The "of N" in the count line: every assessment in the visibility scope. */
export function assessmentScopeTotal(assessments, visibility) {
  return assessments.filter((a) => inVisibility(a, visibility)).length;
}

/** Assessments that pass the filters and search, in the toolbar's sort. */
export function filterAssessments(assessments, filters, scenarioName) {
  const term = filters.search.trim().toLowerCase();
  const out = assessments.filter((a) => {
    if (!inVisibility(a, filters.visibility)) return false;
    if (filters.scenario !== 'all' && a.scenarioId !== filters.scenario) return false;
    if (filters.status !== 'all' && a.status !== filters.status) return false;
    if (!term) return true;
    return [a.name, a.fileName, scenarioName(a.scenarioId)].some((t) => t.toLowerCase().includes(term));
  });
  // The same order as the list's Name header (ListTable sortRows).
  const byName = (x, y) => x.name.localeCompare(y.name, 'en', { numeric: true, sensitivity: 'base' });
  const byDuration = (x, y) => (x.durationSec || 0) - (y.durationSec || 0);
  switch (filters.sort) {
    case 'name_asc': return out.sort(byName);
    case 'name_desc': return out.sort((x, y) => byName(y, x));
    case 'dur_asc': return out.sort(byDuration);
    case 'dur_desc': return out.sort((x, y) => byDuration(y, x));
    case 'date_asc': return out.sort((x, y) => x.uploadedAt.localeCompare(y.uploadedAt));
    default: return out.sort((x, y) => y.uploadedAt.localeCompare(x.uploadedAt));
  }
}

const extensionOf = (name) => (/\.([^.]+)$/.exec(String(name)) || [])[1]?.toLowerCase() || '';
const sameName = (x, y) => String(x).toLowerCase() === String(y).toLowerCase();

/**
 * One picked file, vetted at selection: a wrong type, an oversized file or one
 * already in this selection is refused with a reason, and the rest go ahead. A
 * file name already on an assessment is allowed, since two watches can produce
 * identically named recordings, but it warns and has to be confirmed.
 */
export function vetSourceFile(file, selection, assessments) {
  const entry = { name: file.name, size: file.size || 0, durationSec: null, status: 'uploading', progress: 0 };
  const ext = extensionOf(file.name);
  if (AUDIO_EXTENSIONS.includes(ext)) entry.kind = 'audio';
  else if (TRANSCRIPT_EXTENSIONS.includes(ext)) entry.kind = 'transcript';
  else return { ...entry, reject: `Not an audio or transcript file. Phase 0 accepts ${SOURCE_FILE_LABEL}.` };
  const mb = entry.size / (1024 * 1024);
  if (mb > MAX_SOURCE_FILE_MB) return { ...entry, reject: `Too large at ${mb.toFixed(0)}mb. The limit is ${MAX_SOURCE_FILE_MB}mb.` };
  if (selection.some((e) => !e.reject && sameName(e.name, file.name))) return { ...entry, reject: 'Already selected.' };
  const clash = assessments.find((a) => sameName(a.fileName, file.name));
  if (clash) {
    entry.warn = `Already ${clash.status === 'uploading' ? 'uploading' : 'uploaded'} as ${clash.name}. This will be a second assessment.`;
  }
  return entry;
}

/** The names a save would create: one file takes the name as typed, several take it numbered. */
export function assessmentNamesFor(base, count) {
  const n = Math.max(1, count || 1);
  return n === 1 ? [base] : Array.from({ length: n }, (_, i) => `${base} ${i + 1}`);
}

/**
 * Names already in use, matched ignoring case and outer space. Archived
 * assessments count: the name is still spoken for.
 */
export function assessmentNameClashes(assessments, base, count) {
  const norm = (s) => String(s ?? '').trim().toLowerCase();
  if (!norm(base)) return [];
  const taken = new Set(assessments.map((a) => norm(a.name)));
  return assessmentNamesFor(base.trim(), count).filter((n) => taken.has(norm(n)));
}

/** The pending file's line under its name: size, kind and run time. */
export function pendingFileMeta(entry) {
  return [
    entry.size ? formatFileSize(entry.size) : '0kb',
    entry.kind === 'transcript' ? 'Transcript' : null,
    entry.durationSec ? formatDuration(entry.durationSec) : null,
  ].filter(Boolean).join(' · ');
}

/** "1 upload started." / "3 uploads started." */
export const uploadsStarted = (n) => `${n} ${plural(n, 'upload', 'uploads')} started.`;

const EMPTY_DRAFT = { name: '', scenarioId: null, files: [] };

export class AssessmentsModel {
  /**
   * `library` is the LibraryModel holding the scenarios. `random`, `tickMs`
   * and `now` exist so tests can drive the engines; the app takes the defaults.
   */
  constructor(library, assessments = seedAssessments(), { random = Math.random, tickMs = TICK_MS, now = () => Date.now() } = {}) {
    this.library = library;
    this.assessments = assessments;
    this.view = 'grid';
    this.filters = { ...DEFAULT_ASSESSMENT_FILTERS };
    this.listSort = null; // the list's header sort; null follows the toolbar's Sort
    this.draft = { ...EMPTY_DRAFT };
    this.returnToAdd = false; // came to Settings > Assessments from Add assessment
    this.random = random;
    this.tickMs = tickMs;
    this.now = now;
    this.timer = null;
    this.nextId = 1;
    this.listeners = [];
    this.pushUsage();
  }

  scenarios() {
    return this.library.getState().scenarios || [];
  }

  scenarioName = (id) => this.scenarios().find((s) => s.id === id)?.name || '-';

  setView(view) {
    if (view !== 'grid' && view !== 'list') return;
    this.view = view;
    this.notify();
  }

  setFilter(key, value) {
    if (!(key in this.filters)) return;
    this.filters = { ...this.filters, [key]: value };
    if (key === 'sort') this.listSort = null;
    this.notify();
  }

  setListSort(sort) {
    this.listSort = sort;
    this.filters = { ...this.filters, sort: assessmentToolbarSort(sort, this.filters.sort) };
    this.notify();
  }

  /** No results' way out: search and filters back to how the page opened; the sort and view stay. */
  clearSearchAndFilters() {
    const { scenario, status, search, visibility } = DEFAULT_ASSESSMENT_FILTERS;
    this.filters = { ...this.filters, scenario, status, search, visibility };
    this.notify();
  }

  /** A part-filled form survives being dismissed; files picked keep transferring. */
  setDraft(patch) {
    this.draft = { ...this.draft, ...patch };
    this.notify();
  }

  /**
   * Picked or dropped files join the selection; the accepted ones start
   * transferring at once. Returns the new entries, in the order given.
   */
  addFiles(files) {
    let selection = this.draft.files;
    const added = files.map((file) => {
      const entry = { ...vetSourceFile(file, selection, this.assessments), key: `f${this.nextId++}` };
      selection = [...selection, entry];
      return entry;
    });
    this.setDraft({ files: selection });
    this.start();
    return added;
  }

  /**
   * A recording's run time once the browser has read it; a transcript has none.
   * If the assessment was saved before the read finished, it takes the time too.
   */
  setFileDuration(key, durationSec) {
    this.assessments = this.assessments.map((a) => (a.sourceKey === key ? { ...a, durationSec } : a));
    this.setDraft({ files: this.draft.files.map((e) => (e.key === key ? { ...e, durationSec } : e)) });
  }

  removeFile(key) {
    this.setDraft({ files: this.draft.files.filter((e) => e.key !== key) });
  }

  /**
   * One assessment per accepted file, named as typed or numbered in selection
   * order. Each carries on from wherever its transfer in the dialog got to.
   * Refuses (returns []) without a scenario, a name, a file, or on a name
   * already in use. If the filters would hide the new ones, they clear.
   */
  addAssessments({ name, scenarioId }) {
    const accepted = this.draft.files.filter((e) => !e.reject);
    const base = name.trim();
    if (!scenarioId || !base || !accepted.length) return [];
    if (assessmentNameClashes(this.assessments, base, accepted.length).length) return [];
    const names = assessmentNamesFor(base, accepted.length);
    const created = accepted.map((e, i) => ({
      id: `na${this.nextId++}`, // never an id the Library's new documents use
      name: names[i],
      scenarioId,
      fileName: e.name,
      sourceKey: e.key, // the picked file, so a late run time still reaches it
      source: e.kind,
      durationSec: e.kind === 'transcript' ? null : (e.durationSec || 3600),
      uploadedAt: new Date(this.now()).toISOString(),
      status: e.status === 'uploaded' ? 'queued' : 'uploading',
      progress: Math.round(e.progress || 0),
      auto: true, // the engine carries these the whole way
      stageAt: this.now(),
    }));
    this.assessments = [...created, ...this.assessments];
    this.draft = { ...EMPTY_DRAFT };
    if (created.some((a) => !filterAssessments([a], this.filters, this.scenarioName).length)) this.clearSearchAndFilters();
    this.pushUsage();
    this.notify();
    this.start();
    return created;
  }

  /** Retry re-runs the step that failed: the transfer from zero, or the processing. Returns which. */
  retry(id) {
    const a = this.assessments.find((x) => x.id === id);
    if (!a || a.status !== 'failed' || a.archived) return null;
    const step = a.failedStep === 'processing' ? 'processing' : 'upload';
    this.update(id, step === 'processing'
      ? { status: 'processing', progress: 100, failedStep: undefined, error: undefined, auto: true, stageAt: this.now() }
      : { status: 'uploading', progress: 0, failedStep: undefined, error: undefined, auto: true, stageAt: this.now() });
    this.start();
    return step;
  }

  /** Archived, never deleted; it keeps its status. Uploading has no actions, so it can't be archived. */
  archive(id) {
    const a = this.assessments.find((x) => x.id === id);
    if (!a || a.archived || a.status === 'uploading') return false;
    this.update(id, { archived: true });
    this.pushUsage();
    return true;
  }

  unarchive(id) {
    const a = this.assessments.find((x) => x.id === id);
    if (!a || !a.archived) return false;
    this.update(id, { archived: false });
    this.pushUsage();
    this.start();
    return true;
  }

  /** In-flight transfers fail on an expired session and become retryable. The handoff has no UI trigger for it. */
  expireSession() {
    const live = this.assessments.filter((a) => a.status === 'uploading' && !a.archived);
    live.forEach((a) => this.update(a.id, { status: 'failed', failedStep: 'upload', error: UPLOAD_FAILED }));
    return live.length;
  }

  setReturnToAdd(value) {
    if (this.returnToAdd === value) return;
    this.returnToAdd = value;
    this.notify();
  }

  /** Each scenario's Usage is the active assessments run against it. */
  pushUsage() {
    const counts = {};
    this.assessments.forEach((a) => { if (!a.archived) counts[a.scenarioId] = (counts[a.scenarioId] || 0) + 1; });
    this.library.setScenarioUsage(counts);
  }

  /**
   * One interval for the dialog's transfers and the page's ladder, independent
   * of the view, so leaving the page doesn't stop an upload. It stops itself
   * once nothing moves. Seeded records hold where they were seeded; only ones
   * created or retried this session (`auto`) climb past Queued.
   */
  start() {
    if (this.timer) return;
    this.timer = setInterval(() => this.tick(), this.tickMs);
  }

  tick() {
    const moving = (a) => !a.archived && (a.status === 'uploading' || (a.auto && (a.status === 'queued' || a.status === 'processing')));
    const pending = this.draft.files.some((e) => !e.reject && e.status === 'uploading');
    if (!pending && !this.assessments.some(moving)) {
      this.stop();
      return;
    }
    const now = this.now();
    this.assessments = this.assessments.map((a) => {
      if (!moving(a)) return a;
      if (a.status === 'uploading') {
        const progress = Math.min(100, (a.progress || 0) + 3 + this.random() * 5);
        return progress >= 100 ? { ...a, status: 'queued', progress: 100, stageAt: now } : { ...a, progress };
      }
      if (now - (a.stageAt || 0) < STAGE_MS[a.status]) return a;
      return { ...a, status: a.status === 'queued' ? 'processing' : 'completed', stageAt: now };
    });
    if (pending) {
      this.draft = {
        ...this.draft,
        files: this.draft.files.map((e) => {
          if (e.reject || e.status !== 'uploading') return e;
          const progress = Math.min(100, (e.progress || 0) + 4 + this.random() * 6);
          return progress >= 100 ? { ...e, status: 'uploaded', progress: 100 } : { ...e, progress };
        }),
      };
    }
    this.notify();
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  update(id, patch) {
    this.assessments = this.assessments.map((a) => (a.id === id ? { ...a, ...patch } : a));
    this.notify();
  }

  getState() {
    return {
      assessments: this.assessments,
      view: this.view,
      filters: this.filters,
      listSort: this.listSort || assessmentColumnSort(this.filters.sort),
      draft: this.draft,
      returnToAdd: this.returnToAdd,
    };
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => { this.listeners = this.listeners.filter((l) => l !== listener); };
  }

  notify() {
    const state = this.getState();
    this.listeners.forEach((listener) => listener(state));
  }
}
